import request from 'sb-request'
import actions from 'redux/actions'
import { reducers, getState } from 'redux/core'
import { links, mixpanel, constants, getPrefixedAddressFields } from 'common/helpers'
import { callVindicia, setupBeginPayPal, setupCommitPayPal, setupBeginApplePay,
  setupCommitApplePay, syncUserAndSubscription, callAnalytics } from 'redux/models/payment/util'


const fetch = () => {
  reducers.view.redeemTossInPage.setMainFetchingStatus(true)

  request.post('graphql?name=tossinProducts', {
    body: {
      query: `{
        user {
          canApplyTossinOffer
        }
        tossinProducts {
          productGroups {
            groupName
            price
            discount
            products {
              id
              img
              sex
              name
              brand
              typeGroup
              upchargePrice
              tradingItem {
                id
              }
            }
          }
        }
      }`,
    },
    modifyResult: (response) => {
      let groups         = []
      let isOfferExpired = true

      try {
        const { tossinProducts: { productGroups }, user: { canApplyTossinOffer } } = response

        groups = productGroups.map(({ products, ...rest }) => ({
          ...rest,
          products: products.map(({ tradingItem, id, ...rest }) => ({
            ...rest,
            id: tradingItem ? tradingItem.id : id,
          })),
        }))

        isOfferExpired = !canApplyTossinOffer
      }
      catch (error) {
        console.error(error)
      }

      return {
        groups,
        isOfferExpired,
      }
    },
  })
    .then(
      reducers.view.redeemTossInPage.setMainData,
      () => reducers.view.redeemTossInPage.setMainFetchingStatus(false)
    )
}

const reset = reducers.view.redeemTossInPage.reset // eslint-disable-line prefer-destructuring


// Inquire
const fetchInquire = (values) => {
  let inquireRequest

  if (inquireRequest) {
    inquireRequest.abort()
  }

  inquireRequest = request.get('rest/order/inquire', {
    query: values,
    fixQuery: false,
    multiple: true,
    withErrorModal: false,
    withHumbleErrorModal: false,
    onComplete: () => {
      inquireRequest = null
    },
  })

  return inquireRequest
}

const inquire = (values) => {
  reducers.view.redeemTossInPage.setInquireFetchingStatus(true)

  return fetchInquire(values)
    .then(({ price: total, ...rest }) => {
      reducers.view.redeemTossInPage.setInquireData({ total, ...rest })
    }, () => {
      reducers.view.redeemTossInPage.setInquireFetchingStatus(false)
    })
}

// Credit Card
const paymentWrapper = (action) => action
  .then(
    (result) => {
      const paymentData = {
        paymentType: constants.paymentTypes.ecommerce,
        isTossIn: true,
        ...result,
      }

      syncUserAndSubscription(paymentData)

      mixpanel.track(constants.analyticsEvents.paymentForm.placeOrder, {
        success: true,
      })
    },
    (err) => {
      const { title, message, resData: { isCvvError, authCode } = {} } = err

      const isBillingError = authCode === '713'

      actions.view.paymentPage.setBillingError(isBillingError)

      if (isCvvError) {
        actions.models.modals.open(constants.modals.ErrorCVV)
      }
      else {
        actions.models.modals.open(constants.modals.ServerError, {
          title,
          content: message,
        })
      }

      mixpanel.track(constants.analyticsEvents.paymentForm.placeOrder, {
        success: false,
        reason: `${authCode} | ${title} | ${message}`,
      })

      return Promise.reject(err)
    }
  )
  .then(() => {
    actions.view.paymentPage.setBillingError(false)

    callAnalytics()
    actions.models.router.push(links.thankYou)
    reducers.view.redeemTossInPage.reset()
    actions.models.cart.clear()
  })


const begin = (values) => {
  const { models: { requestTokens: { order: token } } } = getState()
  const { shipping, billing, paymentMethod } = values

  const body = {
    ...getPrefixedAddressFields({ shipping, billing }),
    useSubscriptionShippingAddress: true,
    paymentType: paymentMethod,
    token,
  }

  return request.post('rest/order/begin', {
    body,
    tokenName: constants.requestTokens.order,
  })
}

const commit = (body) => (
  request.post('rest/order/commit', {
    body,
    tokenName: constants.requestTokens.order,
  })
)

const redeem = (values) => {
  const { shipping, billing, creditCard, paymentType } = values

  const action = begin(values)
    .then((data) => (
      callVindicia({
        paymentType,
        shipping,
        billing,
        creditCard,
        ...data,
      })
    ))
    .then(({ orderSession, token }) => commit({ orderSession, token }))

  return paymentWrapper(action)
}


// Buy in one click
const fetchCanBuyInOneClickStatus = () =>
  request.get('rest/order/cart/fast')
    .then(({ canBuyInOneClick } = {}) => {
      reducers.view.redeemTossInPage.setCanBuyInOneClickStatus(canBuyInOneClick)
    })

const buyInOneClick = () => {
  const { models: { requestTokens: { order: token } } } = getState()

  const action = request.post('rest/order/cart/fast', {
    body: { token },
  })

  return paymentWrapper(action)
}


// PayPal
const beginPaymentUsingPayPal = (values) => {
  const beginPayPal = setupBeginPayPal({
    begin,
    sessionName: 'orderSession',
  })

  return beginPayPal(values)
}

const commitPaymentUsingPayPal = (values) => {
  const commitPayPal = setupCommitPayPal({
    commit,
    sessionName: 'orderSession',
  })

  return commitPayPal(values)
    .then(
      () => {
        callAnalytics()
        actions.models.router.push(links.thankYou)
        reducers.view.redeemTossInPage.reset()
        actions.models.cart.clear()
      },
      (err) => Promise.reject(err)
    )
}


// ApplePay
const beginPaymentUsingApplePay = (values) => {
  const beginApplePay = setupBeginApplePay({
    begin,
    tokenName: constants.requestTokens.order,
    sessionName: 'orderSession',
  })

  return beginApplePay(values)
}

const commitPaymentUsingApplePay = (session, values) => {
  const commitApplePay = setupCommitApplePay({
    commit,
    session,
    sessionName: 'orderSession',
    paymentType: constants.paymentTypes.ecommerce,
    tokenName: constants.requestTokens.order,
  })

  return commitApplePay(values)
    .then(
      () => {
        callAnalytics()
        actions.models.router.push(links.thankYou)
        reducers.view.redeemTossInPage.reset()
        actions.models.cart.clear()
      },
      (err) => Promise.reject(err)
    )
}


export default {
  fetch,
  reset,
  redeem,
  inquire,
  fetchInquire,
  buyInOneClick,
  beginPaymentUsingPayPal,
  commitPaymentUsingPayPal,
  beginPaymentUsingApplePay,
  commitPaymentUsingApplePay,
  fetchCanBuyInOneClickStatus,
}
